import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import apiFetch from "./utils/api";
import Typeahead from "./components/Typeahead";
import "./ReportsPage.css";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import ResourceChart from "./ResourceChart";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
);

ModuleRegistry.registerModules([AllCommunityModule]);

const DEFAULT_SHIFT_FLAGS = [false, true, true, true, true, true, false];

const normalizeDaily = (u) => {
  if (!u) return undefined;
  if (typeof u.UserDailyHours !== "undefined" && u.UserDailyHours !== null) {
    return Number(u.UserDailyHours);
  }
  return undefined;
};

const normalizeFlags = (u) => {
  if (u === undefined) return DEFAULT_SHIFT_FLAGS;

  const raw = [
    (u.UserShiftMask & 1) > 0,
    (u.UserShiftMask & 2) > 0,
    (u.UserShiftMask & 4) > 0,
    (u.UserShiftMask & 8) > 0,
    (u.UserShiftMask & 16) > 0,
    (u.UserShiftMask & 32) > 0,
    (u.UserShiftMask & 64) > 0,
  ];
  const flags = raw.map((v) => {
    if (v === null || typeof v === "undefined") return false;
    if (typeof v === "boolean") return v;
    const s = String(v).toLowerCase();
    return s === "1" || s === "true" || s === "y" || s === "yes" || s === "on";
  });
  if (flags.every((f) => !f)) return DEFAULT_SHIFT_FLAGS;
  return flags;
};

const countShiftDaysInRange = (start, end, shiftFlags) => {
  if (!start || !end) return 0;
  const s = new Date(start);
  const e = new Date(end);
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  if (e < s) return 0;
  let count = 0;
  const cur = new Date(s);
  while (cur <= e) {
    const wd = cur.getDay();
    if (shiftFlags && shiftFlags[wd]) count += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};

const ResourceOverview = ({ onClose } = {}) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [months, setMonths] = useState([]);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [resources, setResources] = useState([]);
  const [usersByCode, setUsersByCode] = useState({});

  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null); // 1-12 or null
  const HOURS_PER_DAY = 7.5;
  const [subcodeBreakdown, setSubcodeBreakdown] = useState(null);
  const [projectSubcodeMatrix, setProjectSubcodeMatrix] = useState(null);
  const gridRef = useRef(null);

  const fetchReport = async (y) => {
    setLoading(true);
    setError("");
    setSelectedResource(null);

    try {
      const body = await apiFetch(`/api/reports?year=${y}`);
      setMonths(body.months || []);
      setRows(body.data || []);
    } catch (e) {
      setError(e.message || "Failed to load resource overview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(year);
  }, [year]);

  useEffect(() => {
    (async () => {
      setSelectedLoading(true);
      if (!selectedResource) {
        setSubcodeBreakdown(null);
        setProjectSubcodeMatrix(null);
        return;
      }
      try {
        const r = encodeURIComponent(selectedResource.id);
        const monthParam = selectedMonth ? `&month=${selectedMonth}` : "";
        const res = await apiFetch(
          `/api/reports/resource-subcodes?year=${year}&resource=${r}${monthParam}`,
        );
        setSubcodeBreakdown(res && res.breakdown ? res.breakdown : null);
        const res2 = await apiFetch(
          `/api/reports/resource-project-subcodes?year=${year}&resource=${r}${monthParam}`,
        );
        setProjectSubcodeMatrix(res2 && res2.matrix ? res2 : null);
      } catch (e) {
        setSubcodeBreakdown(null);
        setProjectSubcodeMatrix(null);
      }
      setSelectedLoading(false);
    })();
  }, [selectedResource, year, selectedMonth]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/api/users");
        const list = Array.isArray(res)
          ? res.users || res
          : res && Array.isArray(res.users)
            ? res.users
            : [];
        // normalize to { code, desc } so Typeahead filters by code/desc like projects
        setResources(
          list.map((u) => ({
            id: u.UserID,
            code: u.UserCode,
            desc: u.UserDescription,
          })),
        );
        const map = {};
        list.forEach((u) => {
          const raw = (u.UserCode || u.user || "").toString();
          const code = raw;
          const codeLower = raw.trim().toLowerCase();
          if (code) map[code] = u;
          if (codeLower && !map[codeLower]) map[codeLower] = u;
        });
        setUsersByCode(map);
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  const expectedByUser = useMemo(() => {
    const map = {};
    const monthsCount = months.length || 12;
    const seen = new Set();

    Object.values(usersByCode).forEach((u) => {
      const rawCode = (u && (u.UserCode || u.user || "")).toString();
      const codeKey = rawCode || "";
      if (!codeKey || seen.has(codeKey)) return;
      seen.add(codeKey);

      const daily = normalizeDaily(u);
      const flags = normalizeFlags(u);
      const arr = Array(monthsCount).fill(0);
      for (let mi = 0; mi < monthsCount; mi++) {
        const monthStart = new Date(year, mi, 1);
        const monthEnd = new Date(year, mi + 1, 0);
        const workdays = countShiftDaysInRange(monthStart, monthEnd, flags);
        arr[mi] = typeof daily === "undefined" ? 0 : workdays * Number(daily);
      }

      map[codeKey] = arr;
      map[codeKey.trim().toLowerCase()] = arr;
      const short = codeKey.includes("\\")
        ? codeKey.split("\\").pop()
        : codeKey;
      map[short] = arr;
      map[short.trim().toLowerCase()] = arr;
    });

    return map;
  }, [usersByCode, months, year]);

  const findUserForResource = useCallback(
    (resource) => {
      if (!resource) return null;
      const rraw = String(resource);
      const cand = [
        rraw,
        rraw.trim(),
        rraw.toLowerCase(),
        rraw.trim().toLowerCase(),
      ];
      if (rraw.includes("\\")) {
        const part = rraw.split("\\").pop();
        cand.push(part, part.trim(), part.toLowerCase());
      }
      if (rraw.includes("@")) {
        const local = rraw.split("@")[0];
        cand.push(local, local.trim(), local.toLowerCase());
      }
      for (const c of cand) {
        if (usersByCode && usersByCode[c]) return usersByCode[c];
      }
      const lower = rraw.toLowerCase().trim();
      for (const k of Object.keys(usersByCode || {})) {
        if (!k) continue;
        const kl = String(k).toLowerCase();
        if (kl === lower || kl.endsWith(lower) || kl.includes(lower))
          return usersByCode[k];
      }
      return null;
    },
    [usersByCode],
  );

  const computeUserExpectedForMonth = useCallback(
    (userObj, monthIndex) => {
      const udaily = normalizeDaily(userObj);
      if (typeof udaily === "undefined") return undefined;
      const ms = new Date(year, monthIndex, 1);
      const me = new Date(year, monthIndex + 1, 0);
      const flags = normalizeFlags(userObj);
      const w = countShiftDaysInRange(ms, me, flags);
      return w * Number(udaily);
    },
    [year],
  );

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          !selectedResource ||
          r.resource === (selectedResource.code || selectedResource),
      ),
    [rows, selectedResource],
  );

  const defaultExpectedArr = useMemo(() => {
    const mcount = months.length || 12;
    const def = Array(mcount).fill(0);
    const flagsDefault = [false, true, true, true, true, true, false];
    for (let mi = 0; mi < mcount; mi++) {
      const ms = new Date(year, mi, 1);
      const me = new Date(year, mi + 1, 0);
      const w = countShiftDaysInRange(ms, me, flagsDefault);
      def[mi] = w * 7.5;
    }
    return def;
  }, [months, year]);

  const resolveExpectedArr = useCallback(
    (resource) => {
      if (!resource) return [];
      const cand = [];
      const rraw = String(resource);
      cand.push(
        rraw,
        rraw.trim(),
        rraw.toLowerCase(),
        rraw.trim().toLowerCase(),
      );
      if (rraw.includes("\\")) {
        const part = rraw.split("\\").pop();
        cand.push(part, part.trim(), part.toLowerCase());
      }
      if (rraw.includes("@")) {
        const local = rraw.split("@")[0];
        cand.push(local, local.trim(), local.toLowerCase());
      }
      for (const c of cand) {
        if (expectedByUser[c]) return expectedByUser[c];
      }
      const lower = rraw.toLowerCase().trim();
      for (const k of Object.keys(expectedByUser || {})) {
        if (!k) continue;
        const kl = String(k).toLowerCase();
        if (kl === lower || kl.endsWith(lower) || kl.includes(lower)) {
          return expectedByUser[k];
        }
      }
      return [];
    },
    [expectedByUser],
  );

  const getMonthCellMeta = useCallback(
    (resource, monthIndex, actual) => {
      const expectedArr = resolveExpectedArr(resource);
      const matched = expectedArr && expectedArr.length > 0;
      const finalExpectedArr = matched ? expectedArr : defaultExpectedArr;
      const exp = finalExpectedArr[monthIndex] || 0;
      const monthEnd = new Date(year, monthIndex + 1, 0);
      const now = new Date();
      const isPast = monthEnd < now;

      let cellClass = "";
      if (isPast) {
        if (matched && exp === 0 && actual === 0)
          cellClass = " report-cell--mild";
        if (exp > 0 && actual / exp < 0.9) cellClass = " report-cell--warning";
        if (exp > 0 && actual / exp < 0.7) cellClass = " report-cell--strong";
      }

      let shownExpected = exp;
      const matchedUser = findUserForResource(resource);
      if (matchedUser) {
        const ud = computeUserExpectedForMonth(matchedUser, monthIndex);
        if (typeof ud !== "undefined") shownExpected = ud;
      }

      return {
        cellClass: `report-cell${cellClass}`,
        title: `Expected ${shownExpected.toFixed(2)}h — Actual ${actual.toFixed(2)}h`,
      };
    },
    [
      defaultExpectedArr,
      year,
      resolveExpectedArr,
      findUserForResource,
      computeUserExpectedForMonth,
    ],
  );

  const tableRows = useMemo(() => {
    return filteredRows.map((r) => {
      const data = { resource: r.resource };
      (r.months || []).forEach((val, i) => {
        data[`m_${i}`] = Number(val || 0);
      });
      const rowTotalHours = (r.months || []).reduce(
        (a, b) => a + Number(b || 0),
        0,
      );
      data.totalDays = rowTotalHours / HOURS_PER_DAY;
      data.__resource = r.resource;
      return data;
    });
  }, [filteredRows]);

  const columnDefs = useMemo(() => {
    const monthCols = months.map((m, i) => ({
      headerName: m,
      field: `m_${i}`,
      minWidth: 78,
      cellClass: (params) =>
        getMonthCellMeta(params.data?.__resource, i, Number(params.value || 0))
          .cellClass,
      tooltipValueGetter: (params) =>
        getMonthCellMeta(params.data?.__resource, i, Number(params.value || 0))
          .title,
      valueFormatter: (params) => Number(params.value || 0).toFixed(2),
      cellStyle: { textAlign: "right" },
    }));

    return [
      {
        headerName: "Resource",
        field: "resource",
        minWidth: 120,
        wrapText: true,
        autoHeight: true,
        pinned: "left",
      },
      ...monthCols,
      {
        headerName: "Total (days)",
        field: "totalDays",
        minWidth: 96,
        cellClass: "report-cell",
        valueFormatter: (params) => Number(params.value || 0).toFixed(2),
        cellStyle: { textAlign: "right", fontWeight: 700 },
      },
    ];
  }, [months, getMonthCellMeta]);

  function exportToCSV() {
    const api = gridRef.current?.api;
    if (!api) return;

    const resourceCode = selectedResource?.code
      ? `-${selectedResource.code}`
      : "";
    api.exportDataAsCsv({
      fileName: `resource-overview-${year}${resourceCode}.csv`,
    });
  }

  function exportToPDF() {
    try {
      const baseTitle = `Resource Overview ${selectedResource ? selectedResource.code : ""} ${year}`;
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10);
      const title = `${baseTitle} ${dateStr}`;

      const classToStyle = (cls) => {
        if ((cls || "").includes("report-cell--strong")) {
          return "background:#ffecec;border-left:4px solid #ff4d4d;";
        }
        if ((cls || "").includes("report-cell--warning")) {
          return "background:#fff2e6;border-left:4px solid #ff9f43;";
        }
        if ((cls || "").includes("report-cell--mild")) {
          return "background:#fff7d6;border-left:4px solid #ffcf44;";
        }
        return "";
      };

      const headerHtml = months
        .map(
          (m) =>
            `<th style="border:1px solid #ddd;padding:6px;text-align:left;">${m}</th>`,
        )
        .join("");

      const rowsHtml = (tableRows || [])
        .map((row) => {
          const monthCells = months
            .map((_, i) => {
              const actual = Number(row[`m_${i}`] || 0);
              const meta = getMonthCellMeta(row.__resource, i, actual);
              const extraStyle = classToStyle(meta.cellClass);
              return `<td title="${meta.title}" style="border:1px solid #eee;padding:6px;text-align:right;${extraStyle}">${actual.toFixed(2)}</td>`;
            })
            .join("");

          return `<tr>
            <td style="border:1px solid #eee;padding:6px;">${row.resource || ""}</td>
            ${monthCells}
            <td style="border:1px solid #eee;padding:6px;text-align:right;font-weight:700;">${Number(row.totalDays || 0).toFixed(2)}</td>
          </tr>`;
        })
        .join("");

      const doc = window.open("", "_blank");
      if (!doc) throw new Error("Unable to open print window (popup blocked)");
      doc.document.write(`<!doctype html><html><head><title>${title}</title>`);
      try {
        doc.document.title = title;
      } catch (e) {}
      doc.document.write(
        `<style>body{font-family:Arial,Helvetica,sans-serif;padding:20px} table{border-collapse:collapse;width:100%} th,td{border:1px solid #ddd;padding:6px}</style>`,
      );
      doc.document.write(`</head><body>`);
      doc.document.write(`<h2>${baseTitle}</h2>`);
      doc.document.write(
        `<table><thead><tr><th style="border:1px solid #ddd;padding:6px;text-align:left;">Resource</th>${headerHtml}<th style="border:1px solid #ddd;padding:6px;text-align:left;">Total (days)</th></tr></thead><tbody>${rowsHtml}</tbody></table>`,
      );
      doc.document.write(`</body></html>`);
      doc.document.close();
      setTimeout(() => {
        try {
          doc.print();
        } catch (e) {
          console.warn("Print failed:", e);
        }
        doc.close();
      }, 600);
    } catch (e) {
      console.error(e);
      alert("Export failed: " + (e && e.message ? e.message : String(e)));
    }
  }

  return (
    <div style={{ padding: 20 }}>
      <h2>Resource Overview</h2>

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <label style={{ fontWeight: 600 }}>Year</label>
        <button onClick={() => setYear((y) => y - 1)}>-</button>
        <input
          type="number"
          value={year}
          onChange={(e) =>
            setYear(Number(e.target.value || new Date().getFullYear()))
          }
          style={{ width: 100 }}
        />
        <button onClick={() => setYear((y) => y + 1)}>+</button>
        <div style={{ marginLeft: 12 }}>
          <button onClick={() => fetchReport(year)}>Refresh</button>
        </div>
        <div style={{ marginLeft: 12 }}>
          <button onClick={exportToPDF}>Export to PDF</button>
        </div>
        <div style={{ marginLeft: 12 }}>
          <button onClick={exportToCSV}>Export CSV</button>
        </div>
        <div style={{ marginLeft: 12, minWidth: 260 }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>Resource (search)</div>
          <Typeahead
            items={resources.filter((v) =>
              rows.some((r) => r.resource === v.code),
            )}
            value={selectedResource}
            onChange={(it) => setSelectedResource(it)}
            placeholder="Type resource/user"
            getItemKey={(it) => it.code}
            getItemLabel={(it) => `${it.code} — ${it.desc}`}
            renderItem={(it) => (
              <div>
                <strong style={{ marginRight: 8 }}>{it.code}</strong>
                <span>{it.desc}</span>
              </div>
            )}
          />
        </div>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : error ? (
        <div style={{ color: "darkred" }}>{error}</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <div className="reports-legend" style={{ marginBottom: 8 }}>
            <div className="legend-item">
              <div className="legend-swatch legend-strong" />
              <div>Under-Expected (Shift)</div>
            </div>
            <div className="legend-item">
              <div className="legend-swatch legend-warning" />
              <div>Under-Expected (7.5)</div>
            </div>
            <div className="legend-item">
              <div className="legend-swatch legend-mild" />
              <div>No Shift</div>
            </div>
          </div>
          <div
            className="ag-theme-balham resource-pto-grid resource-overview-grid"
            style={{ width: "100%", minHeight: 120 }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={tableRows}
              columnDefs={columnDefs}
              defaultColDef={{
                sortable: true,
                filter: true,
                resizable: true,
                flex: 1,
                minWidth: 72,
                wrapHeaderText: true,
                autoHeaderHeight: true,
                tooltipShowDelay: 0,
              }}
              domLayout="autoHeight"
              suppressMovableColumns={true}
              onRowClicked={(event) => {
                const code = event?.data?.resource;
                if (!code) return;
                setSelectedResource(
                  resources.find((element) => element.code === code) || {
                    code,
                    desc: code,
                    id: code,
                  },
                );
              }}
              getRowStyle={() => ({ cursor: "pointer" })}
            />
          </div>

          {/* Month selector: sits below the table but above the charts. */}
          {selectedResource && (
            <div style={{ marginTop: 12, marginBottom: 12 }}>
              <label style={{ fontWeight: 600, marginRight: 8 }}>Month</label>
              <select
                value={selectedMonth || ""}
                onChange={(e) =>
                  setSelectedMonth(
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
              >
                <option value="">All</option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
              <label style={{ marginLeft: 8, color: "darkred" }}>
                {selectedLoading ? "Loading..." : ""}
              </label>
            </div>
          )}

          {selectedResource &&
            subcodeBreakdown &&
            subcodeBreakdown.length > 0 && (
              <ResourceChart
                selectedResource={selectedResource}
                subcodeBreakdown={subcodeBreakdown}
                projectSubcodeMatrix={projectSubcodeMatrix}
                year={year}
                month={selectedMonth}
                onClose={onClose}
                loading={setSelectedLoading}
              />
            )}
          {selectedResource &&
            subcodeBreakdown &&
            subcodeBreakdown.length === 0 && (
              <>
                <p>Selected resource has no activity for this period.</p>
              </>
            )}
        </div>
      )}
    </div>
  );
};

export default ResourceOverview;
